import { apiRequest, TokenStorage } from './api';
import { getFirstTouch } from '../attribution/firstTouch';
import { getReferral } from '../attribution/referral';
import { getDeviceTimezone } from '../utils/deviceTimezone';

// First-touch attribution rides along on every call that can create an account.
// The backend only persists it on creation, so sending it on a login is harmless.
function withAttribution(body = {}) {
    // The in-app referral (e.g. a shared Top Movies list) joins the first-touch record.
    const firstTouch = getFirstTouch();
    const referral = getReferral();
    if (!firstTouch && !referral) return body;
    return { ...body, attribution: { ...firstTouch, ...referral } };
}

function withDeviceTimezone(body = {}) {
    return {
        ...body,
        timezone: body.timezone || getDeviceTimezone(),
    };
}

// These return the raw API envelope so AuthContext can inspect `accountDeleted`
export const login           = (body)         => apiRequest('/auth/login',           { method: 'POST', body: withDeviceTimezone(body), authenticated: false });
export const register        = (body)         => apiRequest('/auth/register',         {
    method: 'POST',
    body: withAttribution(withDeviceTimezone(body)),
    authenticated: false,
    headers: { 'X-Aluvo-Client': 'web' },
});
export const googleLogin     = (idToken)      => apiRequest('/auth/google',           { method: 'POST', body: withAttribution(withDeviceTimezone({ idToken })), authenticated: false });
// Apple identity tokens are verified server-side (signature/issuer/audience); the client never decodes them.
export const appleLogin      = ({ identityToken, email, fullName }) => apiRequest('/auth/apple', { method: 'POST', body: withAttribution(withDeviceTimezone({ identityToken, email, fullName })), authenticated: false });
export const logout          = (refreshToken) => apiRequest('/auth/logout',           { method: 'POST', body: { refreshToken } });
export const restoreAccount  = (restoreToken) => apiRequest('/auth/restore-account',  { method: 'POST', body: { restoreToken }, authenticated: false });
// A password change ends every session, this one included, and returns fresh
// credentials for this browser: store them before any other request runs.
export const changePassword = async (body) => {
    const data = await apiRequest('/account/me/password', { method: 'PATCH', body });
    if (data?.tokens?.accessToken && data?.tokens?.refreshToken) {
        TokenStorage.saveTokens(data.tokens.accessToken, data.tokens.refreshToken);
    }
    return data;
};
export const forgotPassword  = (email)        => apiRequest('/auth/forgot-password',  { method: 'POST', body: { email }, authenticated: false });
export const resetPassword   = (token, password) => apiRequest('/auth/reset-password', { method: 'POST', body: { token, password }, authenticated: false });

// ── Email verification ────────────────────────────────────────────────────────
export const resendVerificationEmail = () => apiRequest('/auth/send-verification', { method: 'POST' });
// Re-establish a Google/Apple sign-in that account recovery (or a security
// review) disconnected: the signed-in owner's password plus a fresh provider token.
export const reconnectIdentity = ({ provider, token, currentPassword }) =>
    apiRequest('/account/me/identities/reconnect', { method: 'POST', body: { provider, token, currentPassword } });
// Session-bound: must be called while signed in to the account the link is for.
export const verifyEmail = (token) => apiRequest('/auth/verify-email', { method: 'POST', body: { token } });

// ── Onboarding state (server-side source of truth) ────────────────────────────
export const getOnboardingStatus = () => apiRequest('/onboarding/status');
export const completeOnboarding = ({ skipped = false, step = null } = {}) => apiRequest('/onboarding/complete', { method: 'POST', body: { skipped, step } });
export const getNextQuizRecommendation = () => apiRequest('/onboarding/next-quiz');
export const reportActivationEvent = (eventType, properties = {}) => apiRequest('/analytics/activation', { method: 'POST', body: { eventType, properties } }).catch(() => null);

export const followUser      = (userId)  => apiRequest(`/follows/${userId}`,  { method: 'POST' });
export const unfollowUser    = (userId)  => apiRequest(`/follows/${userId}`,  { method: 'DELETE' });
export const getFollowStatus = (userId)  => apiRequest(`/follows/${userId}/status`);

export const getMyProfile = () => apiRequest('/profile/me');
export const getMyCoins   = () => apiRequest('/profile/me/coins');

// ── Profile picture: curated avatar > uploaded photo > default ──────────────
// Each returns the account's profile-image fields:
// { avatar_url, uploaded_avatar_url, selected_avatar_id, profileImage }.
export const getAvatarLibrary = () => apiRequest('/profile-avatars'); // { avatars, categories }
export const selectAvatar = (avatarId) => apiRequest('/account/avatar', { method: 'PUT', body: { avatarId } });
export const clearSelectedAvatar = () => apiRequest('/account/avatar', { method: 'PUT', body: { avatarId: null } });
export const removeAvatar = () => apiRequest('/account/avatar', { method: 'DELETE' });

export const uploadAvatar = async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const token = (await import('./api')).TokenStorage.getAccessToken();
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
    const res = await fetch(`${API_BASE_URL}/account/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Upload failed');
    return data.data ?? data;
};
